<template>
  <!--
    A real landmark. The SPA has one `role=` across 89 files and no landmarks
    at all (audit_web.md 6.2, S5), so a screen-reader user has no way to jump
    to the navigation. `aria-label` rather than a bare <nav> because some pages
    render in-page nav of their own: an unlabelled landmark in a document with
    more than one is not navigable.
  -->
  <nav aria-label="Main" data-tour="nav.main">
    <ul>
      <li v-for="item in visibleNav" :key="item.name">
        <!--
          `.lw-p-nav__root-link` is a marker, not a style of its own: it lands
          on navLink's rendered root (Vue merges a class passed to a component
          onto its root element regardless of scoping) so navLink.vue can
          hide the label when the rail is collapsed. See navLink.vue's style
          block for the selector this feeds.

          Spec 49 deleted navGroup.vue, the collapsible rail group that no
          entry ever declared: fewer pages with tabs is the direction, not
          more rail entries folded away. An entry is a link, full stop.
        -->
        <nav-link class="lw-p-nav__root-link" :link="item" />
      </li>
    </ul>
  </nav>
</template>

<script>
import navLink from './navLink.vue';
import { SECTIONS } from '../../../router/sections.js';
import { allowedForRole, currentRole, visibleTabs } from '../../../lib/roles.js';
import { MESSAGING_ROLES } from '../../../lib/driverComms.js';

// Spec 49: ten entries. Five of them are SECTIONS (router/sections.js), rail
// entries that own several pages behind a row of section tabs; the rest are
// single pages. A section's entry is derived from its definition so the rail,
// the route table and the tab row can never disagree about what a section is
// called or where it starts. Its link is the section's bare path: the layout
// lands the person on the first tab their role can see.
const section = key => {
  const def = SECTIONS.find(item => item.key === key);
  return { name: def.title, to: { path: def.path }, fa: def.fa, section: def };
};

export default {
  components: { navLink },
  data() {
    return {
      nav: [
        { name: 'Dispatch', to: { name: 'Dispatch' }, fa: 'fa-tachometer' },
        { name: 'Live Map', to: { name: 'Tracking' }, fa: 'fa-map-marker' },
        {
          name: 'Messages',
          to: { name: 'MessagesInbox' },
          fa: 'fa-comments',
          // Shared with every "Message driver" control (lib/driverComms.js).
          roles: MESSAGING_ROLES,
        },
        { name: 'Billing', to: { name: 'Billing' }, fa: 'fa-calculator' },
        section('customers'),
        section('team'),
        section('equipment'),
        section('reports'),
        { name: 'Documents', to: { name: 'Documents' }, fa: 'fa-file-text-o' },
        section('settings'),
      ],
    };
  },
  computed: {
    visibleNav() {
      const role = currentRole(this.$store);
      return this.nav.filter(item =>
        item.section ? visibleTabs(item.section, role).length > 0 : allowedForRole(item, role)
      );
    },
  },
};
</script>

<style scoped>
/*
  Design refresh phase 2, surface 4 (the shell).

  What used to be here and is not any more:

  - `console.log('expanded')`, the `expanded()` method behind it and the
    `@expanded` binding that called it. audit_web.md 3.8 counts it as one of
    four console.log calls shipping in production paths, and the event it
    handled did nothing else.
  - `groupVisible: []`, a data key read by nothing.
  - every rule that paints a nav LINK. Those moved into navLink.vue, which is
    the component that renders the element they style. They had to: a
    parent's scoped CSS reaches a child component's root element and nothing
    deeper, and navLink is rendered as this component's grandchild whenever a
    nav entry has children. See navLink.vue's style block.

  What is left is the list container, which is the only thing this component
  actually renders.
*/
#nav-list ul {
  list-style-type: none;
  padding: 0;
  margin-top: 0;
  color: var(--lw-fg-inverse);
}
</style>
